import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { IconButton, TextField } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { PopupSettings } from "@widgets/commonpopup/PopupSettings";
import { getSearchData } from "../utils/CommonUtils";
import { fnGetDataSet } from "@shared/api/restApi";
import { showPopupModal } from "@widgets/CommonModal";
import { StyleSettings } from "../../styles/StyleSettings";
import SinglePopup from "@widgets/commonpopup/SinglePopup";
import { showDialog } from "@widgets/CommonDialog";
import { ClearIcon } from "@mui/x-date-pickers";
import PostPopup from "@widgets/commonpopup/PostPopup";
import { global } from "@shared/libs/Global";
import { showIssueModal } from "@widgets/IssueModal";
import IssueMenuPopup from "@widgets/commonpopup/IssueMenuPopup";
import VarPropsPopup from "@widgets/commonpopup/VarPropsPopup";

/* ------------------------------------------------------------------
 * 칸 밖에서도 쓰는 코드 찾기 (2026-10-02)
 * 챗봇이 조회조건 코드 칸에 이름을 넣으면, 사람이 치고 Enter 를 누를 때와 같은 순서로 찾는다.
 * 칸 안의 checkData 도 이 함수들을 쓴다 — 찾는 방식을 두 벌 두지 않으려고. 도움창은 칸 안에만 있다(챗봇은 띄우지 않는다).
 * ------------------------------------------------------------------ */

/** 조회 결과 행에 code · name 을 붙인다 — PopupSettings 의 첫 두 칼럼 */
export const mapCodeRows = (code: string, data: any) => {
  const popupSetting = PopupSettings[code];
  if (!popupSetting || !data) return [];
  if (data === "close") {
    return data;
  }
  return data.map((d: any) => ({
    ...d,
    code: d[popupSetting.columns[0]],
    name: d[popupSetting.columns[1]],
  }));
};

/** 글로 코드 찾기 — {팝업}_02(코드 일치) → {팝업}_03(이름). 딱 하나일 때만 그 행, 아니면 null */
export const findSingleCodeRow = async (
  code: string,
  text: string,
  cdClas?: string
): Promise<any | null> => {
  try {
    const popupSetting = PopupSettings[code];
    if (!popupSetting || popupSetting.noLookup) return null;
    const cdFirm = global.getState().userInfo.cd_firm;
    const result1 = await fnGetDataSet({
      id: code.toLowerCase() + "_02",
      params: {
        cd_firm: cdFirm,
        [popupSetting.str_search]: text,
        cd_clas: cdClas || "",
      },
    });
    if (result1[0]?.length === 1) return mapCodeRows(code, result1[0])[0];
    const result2 = await fnGetDataSet({
      id: code.toLowerCase() + "_03",
      params: {
        cd_firm: cdFirm,
        str_search: text,
        cd_clas: cdClas || "",
      },
    });
    if (result2[0]?.length === 1) return mapCodeRows(code, result2[0])[0];
  } catch (e) {}
  return null;
};

interface SnCodeBoxProps {
  defaultValue?: any;
  code: string;
  cd_clas?: string;
  onValueChanged?: (code: string, name: string, rowData?: any) => void;
  isReadOnly?: boolean;
  isRequired?: boolean;
  isEditable?: boolean;
  searchRef?: any;
  inputRef?: any;
  isSearchPanel?: boolean;
  onFocus?: (target: string) => void;
  popupConditions?: any;
}

const SnCodeBox = React.memo(
  ({
    code,
    cd_clas,
    defaultValue,
    onValueChanged,
    isReadOnly = false,
    isRequired,
    isEditable = true,
    searchRef,
    inputRef,
    isSearchPanel = false,
    onFocus,
    popupConditions = {},
  }: SnCodeBoxProps) => {
    const [value, setValue] = useState(defaultValue);
    const [displayValue, setDisplayValue] = useState(defaultValue?.name || "");
    const [borderWidth, setBorderWidth] = useState(isRequired ? "2px" : "1px");
    const [backgroundColor, setBackgroundColor] = useState(
      isEditable ? "white" : "#F1F1F1"
    );

    // tab, enter, blur 이벤트 제어용
    const isOpenRef = useRef(false);
    const valueRef = useRef(defaultValue);
    // const [clearValue, setClearValue] = useState(false);

    // 팝업 세팅용
    const [title, setTitle] = useState("");
    const [width, setWidth] = useState<any>();
    const [height, setHeight] = useState<any>();
    const [autoSearch, setAutoSearch] = useState(true);

    // x버튼 조작용 state
    const [isMouseOver, setMouseOver] = useState(false);
    const [isFocused, setFocused] = useState(false);

    useEffect(() => {
      const singlePopupData = getSearchData(code, undefined);
      setTitle(singlePopupData.title);
      setAutoSearch(singlePopupData.autoSearch);
      setWidth(singlePopupData.width);
      setHeight(singlePopupData.height);
    }, [code]);

    useEffect(() => {
      if (defaultValue) {
        setValue(defaultValue);
        valueRef.current = defaultValue;
        if (isFocused) {
          setDisplayValue(defaultValue?.code || "");
        } else {
          setDisplayValue(defaultValue?.name || "");
        }
      }
    }, [defaultValue]);

    useEffect(() => setBorderWidth(isRequired ? "2px" : "1px"), [isRequired]);

    useEffect(
      () => setBackgroundColor(isEditable ? "white" : "#F1F1F1"),
      [isEditable]
    );

    const openPopup = async (inputValue?: string) => {
      if (isReadOnly) return showDialog("alter", "읽기 전용입니다.");
      if (isOpenRef.current) return;

      isOpenRef.current = true;
      try {
        const formData = searchRef?.current?.getFormdata() || {};
        const mergedConditions = popupConditions
          ? { ...formData, ...popupConditions }
          : formData;

        const mergedData = {
          ...mergedConditions,
          str_search: inputValue || displayValue,
          cd_clas: cd_clas || "",
        };

        let data: any;
        if (code === "POPUP_POSTCODE") {
          const result = await showPopupModal({
            title: title,
            children: <PostPopup />,
            width: width,
            height: height,
            okayBtn: false,
          });
          data = handleReturnFunction(result);
          if (data === "close") {
            return;
          } else if (data.length > 0) {
            setValue(data[0]);
            valueRef.current = data[0];
            setDisplayValue(data[0].name);
            onValueChanged?.(data[0].code, data[0].name, data[0]);
            onFocus && onFocus("next");
          } else {
            // 팝업에서 취소 눌렀을 때
            onFocus && onFocus("current");
          }
        } else if (code === "POPUP_VARPROPS") {
          // 조회 도움창이 아니라 직접 입력 팝업 (ID/Type/Default/Format).
          // validation:true → 팝업의 checkValidity가 false면 닫히지 않는다.
          const result = await showPopupModal({
            title: title,
            children: (
              // 재오픈 시 기존 값이 채워지도록. 처음엔 code(=id)만 있을 수 있어 보정한다.
              <VarPropsPopup
                defaultValue={{
                  ...valueRef.current,
                  id: valueRef.current?.id ?? valueRef.current?.code ?? "",
                }}
              />
            ),
            width: width,
            height: height,
            validation: true,
          });
          data = handleReturnFunction(result);
          if (data === "close") {
            return;
          } else if (data.length > 0) {
            setValue(data[0]);
            valueRef.current = data[0];
            setDisplayValue(data[0].name);
            onValueChanged?.(data[0].code, data[0].name, data[0]);
            onFocus && onFocus("next");
          } else {
            clearValue();
          }
        } else if (code === "POPUP_MENU") {
          const issueMenuPopup = (
            <IssueMenuPopup code={code} selectedData={mergedData} />
          );
          const result = await showIssueModal({
            code: code,
            title: title,
            children: issueMenuPopup,
            width: width,
            height: height,
          });
          data = handleReturnFunction(result);
          if (data === "close") {
            // setValue({ code: displayValue, name: "" });
            // setDisplayValue(displayValue);
            // onValueChanged?.(displayValue, "");
            // onFocus && onFocus("current");
            return;
          } else if (data.length > 0) {
            setValue(data[0]);
            valueRef.current = data[0];
            if (isSearchPanel) {
              setDisplayValue(data[0].code);
              onValueChanged?.(data[0].code, data[0].name, data[0]);
              onFocus && onFocus("current");
            } else {
              setDisplayValue(data[0].name);
              onValueChanged?.(data[0].code, data[0].name, data[0]);
              onFocus && onFocus("next");
            }
          } else {
            // 팝업에서 취소 눌렀을 때
            clearValue();
          }
        } else {
          const result = await showPopupModal({
            title: title,
            children: (
              <SinglePopup
                code={code}
                selectedData={mergedData}
                autoSearch={autoSearch}
              />
            ),
            width: width,
            height: height,
          });
          data = handleReturnFunction(result);
          if (data === "close") {
            // setValue({ code: displayValue, name: "" });
            // setDisplayValue(displayValue);
            // onValueChanged?.(displayValue, "");
            // onFocus && onFocus("current");
            return;
          } else if (data.length > 0) {
            setValue(data[0]);
            valueRef.current = data[0];
            if (isSearchPanel) {
              setDisplayValue(data[0].code);
              onValueChanged?.(data[0].code, data[0].name);
              onFocus && onFocus("current");
            } else {
              setDisplayValue(data[0].name);
              onValueChanged?.(data[0].code, data[0].name, data[0]);
              onFocus && onFocus("next");
            }
          } else {
            // 팝업에서 취소 눌렀을 때
            clearValue();
          }
        }
      } finally {
        setTimeout(() => {
          isOpenRef.current = false;
        }, 100);
      }
    };

    const handleFocus = () => {
      setFocused(true);
      setDisplayValue(value.code);
    };

    const handleBlur = async (key?: string) => {
      setFocused(false);

      // 이미 팝업 열려있으면 return
      if (isOpenRef.current) return;

      if (code === "POPUP_POSTCODE") {
        if ((key === "Enter" || key === "searchBtn") && !isOpenRef.current) {
          await openPopup();
        } else {
          if (key === "Tab") onFocus && onFocus("next");
        }
      } else {
        if (!displayValue) {
          // 입력값이 없을 때
          if ((key === "Enter" || key === "searchBtn") && !isOpenRef.current) {
            await openPopup();
          } else {
            // setClearValue(true);
            clearValue();
            if (key === "Tab") onFocus && onFocus("next");
          }
        } else {
          // 입력값이 있을 때
          if (key === "searchBtn" && !isOpenRef.current) {
            await openPopup(value.code || displayValue);
          } else {
            const data = await checkData();
            if (data && data.length === 1) {
              setValue(data[0]);
              valueRef.current = data[0];
              onValueChanged?.(data[0].code, data[0].name, data[0]);
              if (key === "Enter") {
                if (isSearchPanel) {
                  setDisplayValue(data[0].code);
                  setFocused(true);
                  onFocus && onFocus("current");
                } else {
                  setDisplayValue(data[0].name);
                  onFocus && onFocus("next");
                }
              } else if (key === "Tab") {
                setDisplayValue(data[0].name);
                onFocus && onFocus("next");
              } else {
                setDisplayValue(data[0].name);
              }
            } else if (!isOpenRef.current) {
              await openPopup(displayValue);
            }
          }
        }
      }
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      setDisplayValue(e.target.value);
    };

    const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        await handleBlur(e.key);
      }
    };

    const checkData = async () => {
      try {
        const popupSetting = PopupSettings[code];
        if (!popupSetting || value?.name === displayValue) return;
        // 직접 입력 팝업은 {code}_02/_03 조회 쿼리가 없다. 그대로 두면 blur마다
        // 없는 쿼리를 두 번씩 호출해 서버 오류만 쌓이므로 건너뛴다.
        if (popupSetting.noLookup) return;

        const row = await findSingleCodeRow(code, displayValue, cd_clas);
        if (row) return [row];
      } catch (e) {}
    };

    const handleReturnFunction = (data: any) => mapCodeRows(code, data);

    const handleClear = async (e: any) => {
      e.preventDefault();
      e.stopPropagation();
      // setClearValue(true);
      setTimeout(() => {
        clearValue();
      }, 0);
      onFocus && onFocus("current");
    };

    const clearValue = () => {
      if (isReadOnly) return showDialog("alter", "읽기 전용입니다.");
      onValueChanged?.("", "", {});
      setValue({ code: "", name: "" });
      valueRef.current = { code: "", name: "" };
      setDisplayValue("");
      // if (index && inputEls) inputEls.current[index].value = "";
    };

    return (
      <div className="relative w-full" style={{ padding: 0 }}>
        <TextField
          inputRef={inputRef}
          value={displayValue}
          onFocus={handleFocus}
          onBlur={() => handleBlur()}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onMouseOver={() => setMouseOver(true)}
          onMouseOut={() => setMouseOver(false)}
          fullWidth
          disabled={isReadOnly}
          slotProps={{
            input: {
              autoComplete: "off",
              readOnly: isReadOnly,
              endAdornment: (
                <div style={{ display: "flex", alignItems: "center" }}>
                  {(isFocused || isMouseOver) &&
                    !isReadOnly &&
                    displayValue && (
                      <IconButton
                        edge="end"
                        onClick={handleClear}
                        onMouseDown={(event) => event.preventDefault()}
                        size="small"
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    )}
                  <IconButton
                    onClick={(e) => {
                      e.preventDefault();
                      handleBlur("searchBtn");
                    }}
                    edge="end"
                    tabIndex={-1}
                    size="small"
                  >
                    <SearchIcon fontSize="small" />
                  </IconButton>
                </div>
              ),
            },
          }}
          sx={{
            backgroundColor,
            borderWidth,
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderWidth: borderWidth, // 테두리 두께 설정
              },
              "&:hover fieldset": {
                borderWidth: borderWidth, // 호버 시 두께 유지
              },
              "&.Mui-focused fieldset": {
                borderWidth: borderWidth, // 포커스 시 두께 유지
              },
            },
            "& .MuiInputBase-root": {
              height: StyleSettings.control_height,
              paddingRight: 1,
              "&.Mui-disabled": {
                backgroundColor: "#f5f5f5",
              },
            },
            "& .MuiInputBase-input": {
              fontSize: StyleSettings.font,
            },
          }}
        />
      </div>
    );
  }
);
export default SnCodeBox;
